import React, { useState, useEffect, useRef } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import './InputPlaces.scss';

type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value'
> & {
  as?: 'input';
  type?: string;
  id: string;
  label: string;
  min?: number | string;
  max?: number | string;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
  icon?: string;
  wrapperClass?: string;
  inputClass?: string;
  labelClass?: string;
};

const API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY as string;

const PlaceAutocomplete = ({
  onPlaceSelect,
  id,
  label,
  value,
  onChange,
  icon,
  inputClass = '',
  labelClass = '',
  ...rest
}: InputProps) => {
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary('places');

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: ['address_components', 'name'],
      strictBounds: false,
      componentRestrictions: { country: "usa" },
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    const listener = placeAutocomplete.addListener('place_changed', () => {
      onPlaceSelect(placeAutocomplete.getPlace());
    });

    return () => {
      google.maps.event.removeListener(listener);
    }
  }, [onPlaceSelect, placeAutocomplete]);

  const baseInputClass = `floating-input__field ${inputClass}`;
  const baseLabelClass = `floating-input__label ${labelClass}`;

  return (
    <div className="autocomplete-container">
      <input
        ref={inputRef}
        id={id}
        placeholder=" "
        className={baseInputClass}
        value={value ?? ''}
        onChange={onChange}
        {...rest}
      />
       <label htmlFor={id} className={baseLabelClass}>
         {label}
       </label>
       {icon && (
         <img 
           src={icon} 
           alt="icon" 
           className="absolute right-3 top-1/2 transform -translate-y-1/2 w-8 h-8"
           style={{ filter: 'brightness(0) saturate(100%) invert(78%) sepia(8%) saturate(1000%) hue-rotate(25deg) brightness(95%) contrast(88%)' }}
         />
       )}
    </div>
  );
};

export const FloatingPlacesInput = (props: InputProps) => {
  const { wrapperClass = '' } = props;
  const baseWrapperClass = `floating-input ${wrapperClass}`;

  return (
    <div className={baseWrapperClass}>
      <APIProvider apiKey={API_KEY}>
        <PlaceAutocomplete {...props} />
      </APIProvider>
    </div>
  );
};

FloatingPlacesInput.displayName = 'FloatingPlacesInput';
